import { IconName } from "@zenchef/icomoon";
import {
  ToggleGroup,
  ToggleGroupItemBaseProps,
  ToggleGroupRootBaseProps,
} from "@ark-ui/react";
import {
  aromaToggleButtons,
  AromaToggleButtonsVariantProps,
} from "@zenchef/styled-system/recipes";
import withTestId from "../../utils/withTestId";
import Icon from "../Icon/Icon";
import { FieldProps } from "../../utils/types";
import { PropsWithChildren } from "react";
import { Box, Flex } from "@zenchef/styled-system/jsx";
import { Text } from "../typography/Text";
import { cx } from "@zenchef/styled-system/css";

export interface ToggleButtonProps
  extends AromaToggleButtonsVariantProps,
    ToggleGroupItemBaseProps,
    FieldProps {
  label?: string;
  iconName?: IconName;
  itemClassName?: string;
}

const AromaToggleButton = ({
  value,
  label,
  iconName,
  itemClassName,
  ...propsWithVariants
}: ToggleButtonProps) => {
  const [variants] = aromaToggleButtons.splitVariantProps(propsWithVariants);
  const classes = aromaToggleButtons(variants);

  return (
    <ToggleGroup.Item value={value} className={cx(classes.item, itemClassName)}>
      {iconName && <Icon name={iconName} />}
      {label && label}
    </ToggleGroup.Item>
  );
};

export interface ToggleGroupProps<T extends string>
  extends AromaToggleButtonsVariantProps,
    ToggleGroupRootBaseProps,
    PropsWithChildren,
    FieldProps {
  onChange: (value: T[]) => void;
  value: T[] | undefined;
}

function AromaToggleGroup<T extends string = string>({
  multiple,
  defaultValue,
  value,
  disabled,
  children,
  onChange,
  ...propsWithVariants
}: ToggleGroupProps<T>) {
  const [variants, props] =
    aromaToggleButtons.splitVariantProps(propsWithVariants);
  const classes = aromaToggleButtons(variants);

  return (
    <ToggleGroup.Root
      multiple={multiple}
      defaultValue={defaultValue}
      value={value}
      disabled={disabled}
      onValueChange={({ value }) => onChange(value as T[])}
      className={classes.container}
      {...props}
    >
      {children}
    </ToggleGroup.Root>
  );
}

export interface ToggleButtonsProps
  extends AromaToggleButtonsVariantProps,
    PropsWithChildren {
  title?: string;
  subtitle?: string;
  hintText?: string;
}

const AromaToggleButtons = ({
  title,
  subtitle,
  hintText,
  children,
  ...propsWithVariants
}: ToggleButtonsProps) => {
  const [variants] = aromaToggleButtons.splitVariantProps(propsWithVariants);
  const classes = aromaToggleButtons(variants);

  return (
    <Box>
      <Flex className={classes.wrapper}>
        {title && <Text textStyle="title.s">{title}</Text>}
        {subtitle && <Text textStyle="paragraph.m.regular">{subtitle}</Text>}
      </Flex>
      {children}
      {hintText && <Text textStyle="paragraph.m.regular">{hintText}</Text>}
    </Box>
  );
};

AromaToggleButtons.Item = withTestId("btn")(AromaToggleButton);
AromaToggleButtons.Group = AromaToggleGroup;

export default AromaToggleButtons;
